import autoTable from 'jspdf-autotable';

import { formatDate, formatMoney } from '../../../../../../lib/functions/utils';
import { BasePDFGenerator, PDFGeneratorConfig } from '../reportUtils/base';
import { getShortString } from '../reportUtils/documentUtils';

interface NomineeReportConfig extends PDFGeneratorConfig {
  nominee: any;
}

export class NomineeReportGenerator extends BasePDFGenerator {
  private nominee: any;

  constructor(config: NomineeReportConfig) {
    super({ ...config, orientation: 'landscape' });
    this.nominee = config.nominee;
  }

  protected addSummarySection(): void {
    const nominee = this.nominee;
    this.doc.setFontSize(10);
    const col1X = 10;
    const col2X = 115;
    const col3X = 210;
    const defaultX = 35;
    const initialY = 30;

    // Calcular promedios y totales
    const resells = nominee.resells || [];
    const totalAmount = resells.reduce(
      (acc: number, resell: any) => acc + (resell.amount || 0),
      0,
    );
    const totalOriginalAmount = resells.reduce(
      (acc: number, resell: any) =>
        acc + (resell.originalAmount || resell.amount || 0),
      0,
    );

    // Calcular diferencia precio total
    const totalRateCost = resells.reduce(
      (acc: number, resell: any) => acc + (resell.rateCost || 0),
      0,
    );

    const fundName =
      resells.length > 0
        ? resells[0].fundName || nominee.fundId || 'N/A'
        : nominee.fundId || 'N/A';

    // Columna 1
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('ID Nómina', col1X, initialY);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(`: ${nominee.id}`, col1X + defaultX, initialY);

    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Fecha de operación', col1X, initialY + 5);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(
      `: ${nominee.operationDate ? formatDate(new Date(nominee.operationDate)) : 'N/A'}`,
      col1X + defaultX,
      initialY + 5,
    );

    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Fecha de creación', col1X, initialY + 10);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(
      `: ${nominee.createdAt ? formatDate(new Date(nominee.createdAt)) : 'N/A'}`,
      col1X + defaultX,
      initialY + 10,
    );

    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Fondo', col1X, initialY + 15);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(`: ${fundName}`, col1X + defaultX, initialY + 15);

    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Cantidad de docs', col1X, initialY + 20);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(`: ${resells.length}`, col1X + defaultX, initialY + 20);

    // Columna 2
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Monto original total', col2X, initialY);
    this.doc.setFont('helvetica', 'normal');
    const alignRight = (text: string, x: number, y: number) => {
      const textWidth = this.doc.getTextWidth(text);
      this.doc.text(':', x + defaultX - 25, y);
      this.doc.text(text, x + defaultX - textWidth, y);
    };
    alignRight(formatMoney(totalOriginalAmount), col2X + defaultX, initialY);

    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Monto total nómina', col2X, initialY + 5);
    this.doc.setFont('helvetica', 'normal');
    alignRight(formatMoney(totalAmount), col2X + defaultX, initialY + 5);

    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Diferencia precio total', col2X, initialY + 10);
    this.doc.setFont('helvetica', 'normal');
    alignRight(formatMoney(totalRateCost), col2X + defaultX, initialY + 10);

    // Columna 3
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Comisión + IVA total', col3X, initialY);
    this.doc.setFont('helvetica', 'normal');
    alignRight(
      formatMoney(
        resells.reduce(
          (acc: number, resell: any) =>
            acc +
            ((resell.operationCostFixed || 0) +
              (resell.operationCostVariable || 0)) *
              1.19,
          0,
        ),
      ),
      col3X + defaultX,
      initialY,
    );

    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Monto a aplicar', col3X, initialY + 5);
    this.doc.setFont('helvetica', 'normal');
    alignRight(
      formatMoney(nominee.amountToApply || 0),
      col3X + defaultX,
      initialY + 5,
    );

    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Monto a recibir', col3X, initialY + 10);
    this.doc.setFont('helvetica', 'normal');
    alignRight(
      formatMoney(nominee.amountToReceive || 0),
      col3X + defaultX,
      initialY + 10,
    );
  }

  protected addContentSection(): void {
    const startY = 65;
    let currentY = startY;

    // Tabla de resells (documentos en la nómina)
    if (this.nominee.resells && this.nominee.resells.length > 0) {
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Documentos en la nómina', 10, currentY);
      this.doc.setFont('helvetica', 'normal');

      autoTable(this.doc, {
        startY: currentY + 5,
        head: [
          [
            'Folio',
            'Tipo',
            'Cliente',
            'Deudor',
            'Monto Original',
            'Monto Financiado',
            '% Financ.',
            'Tasa',
            'Diferencia precio',
            'Comisión + IVA',
            'Fecha inicio',
            'Fecha término',
            'Días',
          ],
        ],
        body: this.nominee.resells.map((resell: any) => {
          const originalAmount = resell.originalAmount || resell.amount || 0;
          const advance = resell.advance || 100;
          const financedAmount = originalAmount * (advance / 100);

          return [
            resell.folio || resell.document?.folio || 'N/A',
            resell.docType || resell.document?.subType || 'N/A',
            `${resell.businessIdentifier || ''}\n${getShortString(resell.businessName || '', 20)}`,
            `${resell.stakeholderIdentifier || ''}\n${getShortString(resell.stakeholderName || '', 20)}`,
            formatMoney(originalAmount),
            formatMoney(financedAmount),
            `${advance}%`,
            `${resell.rate || 0}%`,
            formatMoney(resell.rateCost || 0),
            formatMoney(
              ((resell.operationCostFixed || 0) +
                (resell.operationCostVariable || 0)) *
                1.19,
            ),
            resell.startDate ? formatDate(new Date(resell.startDate)) : 'N/A',
            resell.endDate ? formatDate(new Date(resell.endDate)) : 'N/A',
            String(resell.days || 0),
          ];
        }),
        theme: 'grid',
        headStyles: {
          fillColor: '#efefef',
          textColor: '#1b1b1b',
          fontStyle: 'bold',
          halign: 'left',
          valign: 'middle',
          lineColor: [44, 62, 80],
          fontSize: 9,
        },
        bodyStyles: {
          textColor: [50, 50, 50],
          fontSize: 8,
        },
        styles: {
          font: 'helvetica',
          lineColor: [44, 62, 80],
          lineWidth: 0.1,
        },
      });

      currentY = (this.doc as any).lastAutoTable
        ? (this.doc as any).lastAutoTable.finalY + 10
        : currentY + 50;
    }

    // Tabla de applications (aplicaciones)
    if (this.nominee.applications && this.nominee.applications.length > 0) {
      if (currentY > 150) {
        this.doc.addPage();
        currentY = 30;
      }

      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Aplicaciones', 10, currentY);
      this.doc.setFont('helvetica', 'normal');

      autoTable(this.doc, {
        startY: currentY + 5,
        head: [
          [
            'ID',
            'Monto',
            '% Financ.',
            'Tasa',
            'Diferencia precio',
            'Comisión',
            'Comisión + IVA',
            'Fecha inicio',
            'Fecha término',
            'Monto a conciliar',
          ],
        ],
        body: this.nominee.applications.map((application: any) => {
          const advance = application.advance || 100;
          const operationCostWithIVA =
            ((application.operationCostFixed || 0) +
              (application.operationCostVariable || 0)) *
            1.19;

          return [
            String(application.id || 'N/A'),
            formatMoney(application.amount || 0),
            `${advance}%`,
            `${application.rate || 0}%`,
            formatMoney(application.rateCost || 0),
            formatMoney(
              (application.operationCostFixed || 0) +
                (application.operationCostVariable || 0),
            ),
            formatMoney(operationCostWithIVA),
            application.startDate
              ? formatDate(new Date(application.startDate))
              : 'N/A',
            application.dueDate
              ? formatDate(new Date(application.dueDate))
              : 'N/A',
            formatMoney(application.amountToConciliate || 0),
          ];
        }),
        theme: 'grid',
        headStyles: {
          fillColor: '#efefef',
          textColor: '#1b1b1b',
          fontStyle: 'bold',
          halign: 'left',
          valign: 'middle',
          lineColor: [44, 62, 80],
          fontSize: 9,
        },
        bodyStyles: {
          textColor: [50, 50, 50],
          fontSize: 8,
        },
        styles: {
          font: 'helvetica',
          lineColor: [44, 62, 80],
          lineWidth: 0.1,
        },
      });

      currentY = (this.doc as any).lastAutoTable
        ? (this.doc as any).lastAutoTable.finalY + 10
        : currentY + 50;
    }
  }

  public async generate() {
    await this.addTitleSection();
    this.addSummarySection();
    this.addContentSection();
    this.saveDocument();
  }
}
